import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Button, SearchBar, Tooltip } from 'common/components';
import { CallTypeCategories } from 'types/call.type';
import { useGetCallDispositions } from 'services/hooks/queries/useGetCallDispositions';
import { formatCallOutcome, formatDisposition } from '../../../utils';
import type { CallsTableFilterName, TableFilterOption } from '../../CallsTable/CallsTable';
import { CALL_ANALYTICS_DIRECTION_OPTIONS } from './CallAnalyticsFilters.constants';
import { CallAnalyticsMultiSelectFilter } from './CallAnalyticsMultiSelectFilter';
import { CallAnalyticsDateRangeSelector } from './CallAnalyticsDateRangeSelector';
import { useFixedOnScroll } from './hooks';

import styles from './CallAnalyticsFilters.module.scss';
export interface CallAnalyticsFiltersProps {
    startDate: Date | null;
    endDate: Date | null;
    filterCallStatus: string;
    filterLeadStatus: string;
    filterCallDirection: string;
    filterDisposition: string;
    partyStages?: string[];
    onSearchChange: (value: string) => void;
    onDateRangeTypeChange: (range: string) => void;
    onDateRangeChange: (dateRange: [Date | null, Date | null]) => void;
    onFilterChange: (filter: CallsTableFilterName, value: string) => void;
    onClear: () => void;
    actionsSlot?: React.ReactNode;
}

export const CallAnalyticsFilters: React.FC<CallAnalyticsFiltersProps> = ({
    startDate,
    endDate,
    filterCallStatus,
    filterLeadStatus,
    filterCallDirection,
    filterDisposition,
    partyStages = [],
    onSearchChange,
    onDateRangeTypeChange,
    onDateRangeChange,
    onFilterChange,
    onClear,
    actionsSlot,
}) => {
    const [searchBarKey, setSearchBarKey] = useState(0);
    const { callDispositions } = useGetCallDispositions();

    const handleClear = () => {
        setSearchBarKey((key) => key + 1);
        onClear();
    };

    const statusOptions = useMemo<TableFilterOption[]>(
        () =>
            Object.keys(CallTypeCategories).map((outcome) => ({
                label: formatCallOutcome(outcome),
                value: outcome,
                category: CallTypeCategories[outcome as keyof typeof CallTypeCategories],
            })),
        []
    );

    const partyStageOptions = useMemo<TableFilterOption[]>(
        () => partyStages.map((stage) => ({ value: stage, label: stage })),
        [partyStages]
    );

    const dispositionOptions = useMemo<TableFilterOption[]>(
        () =>
            callDispositions?.dispositions.map((d) => ({
                label: formatDisposition(d),
                value: d,
            })) ?? [],
        [callDispositions]
    );

    const {
        containerRef,
        targetRef,
        className: fixedClassName,
        style: fixedStyle,
        placeholderHeight,
    } = useFixedOnScroll(styles.filterBarFixed);

    return (
        <div ref={containerRef} className={styles.filterBarContainer}>
            {placeholderHeight > 0 ? (
                <div className={styles.filterBarPlaceholder} style={{ height: placeholderHeight }} aria-hidden='true' />
            ) : null}
            <div
                ref={targetRef}
                className={`${styles.filterBar} ${fixedClassName}`.trim()}
                style={fixedStyle}
                data-testid='call-analytics-filters'
            >
                <div className={`${styles.filterControl} ${styles.searchBox}`}>
                    <SearchBar
                        key={searchBarKey}
                        className={styles.searchBar}
                        placeholder='Search by name, number or ID'
                        onSearch={onSearchChange}
                    />
                </div>

                <div className={`${styles.filterControl} ${styles.dateFilterControl}`}>
                    <CallAnalyticsDateRangeSelector
                        startDate={startDate}
                        endDate={endDate}
                        onDateRangeTypeChange={onDateRangeTypeChange}
                        onDateRangeChange={onDateRangeChange}
                    />
                </div>

                <div className={styles.filterControl} data-testid='call-direction-filter'>
                    <CallAnalyticsMultiSelectFilter
                        label='Call direction'
                        allOptionLabel='All Call Directions'
                        selectedValues={[filterCallDirection]}
                        onChange={(values) => onFilterChange('callDirection', values[0] ?? '')}
                        options={CALL_ANALYTICS_DIRECTION_OPTIONS.map((option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        isMulti={false}
                        aria-label='Call direction filter'
                    />
                </div>

                <div className={styles.filterControl}>
                    <CallAnalyticsMultiSelectFilter
                        label='Call status'
                        options={statusOptions}
                        allOptionLabel='All statuses'
                        selectedValues={filterCallStatus ? filterCallStatus.split(',').filter(Boolean) : []}
                        onChange={(values) => onFilterChange('callStatus', values.join(','))}
                        isSearchable
                        aria-label='Call status filter'
                    />
                </div>

                <div className={styles.filterControl}>
                    <CallAnalyticsMultiSelectFilter
                        label='Party stage'
                        isSearchable
                        allOptionLabel='All party stages'
                        options={partyStageOptions}
                        selectedValues={filterLeadStatus ? filterLeadStatus.split(',').filter(Boolean) : []}
                        onChange={(values) => onFilterChange('leadStatus', values.join(','))}
                        aria-label='Party stage filter'
                    />
                </div>

                <div className={styles.filterControl}>
                    <CallAnalyticsMultiSelectFilter
                        label='Disposition'
                        isSearchable
                        allOptionLabel='All dispositions'
                        options={dispositionOptions}
                        selectedValues={filterDisposition ? filterDisposition.split(',').filter(Boolean) : []}
                        onChange={(values) => onFilterChange('disposition', values.join(','))}
                        aria-label='Disposition filter'
                    />
                </div>

                <div className={styles.actions}>
                    <Tooltip content='Clear all filters' position='bottom'>
                        <Button variant='outline-primary' icon={X} onClick={handleClear} iconOnly />
                    </Tooltip>
                    {actionsSlot}
                </div>
            </div>
        </div>
    );
};
